import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from 'react-router';
import { ChangeEvent, SubmitEvent, useCallback, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import { MenuItem } from '@mui/material';
import { PackagingType, type ProductInventoryItem } from '../../models/ProductInventoryItem';
import type { Batch } from '../../models/Batch';
import NumberField from '../NumberField';
import { useDialogs } from '../../hooks/useDialogs/useDialogs';
import { useMutation } from '@apollo/client/react';
import { GET_PRODUCT_BY_ID_QUERY, GET_PRODUCTS_QUERY } from '../../queries/ProductQueries';
import { DELETE_PRODUCT_INVENTORY_ITEM_BY_ID_MUTATION } from '../../mutations/ProductInventoryItemMutation';
import useNotifications from '../../hooks/useNotifications/useNotifications';

export interface ProductInventoryItemFormState {
  values: Partial<Omit<ProductInventoryItem, 'id'>>;
  errors: Partial<Record<keyof ProductInventoryItemFormState['values'], string>>;
}

export interface ProductInventoryItemFormProps {
  productInventoryItemId?: number;
  productId: number;
  batches: Batch[];
  formState: ProductInventoryItemFormState;
  onFieldChange: (
    name: keyof ProductInventoryItemFormState['values'],
    value: FormFieldValue,
    index?: number
  ) => void;
  onSubmit: (formValues: Partial<ProductInventoryItemFormState['values']>) => Promise<void>;
  onReset?: (formValues: Partial<ProductInventoryItemFormState['values']>) => void;
  submitButtonLabel: string;
  backButtonPath?: string;
}

export default function ProductInventoryItemForm(props: ProductInventoryItemFormProps) {
  const {
    productInventoryItemId,
    productId,
    batches,
    formState,
    onFieldChange,
    onSubmit,
    onReset,
    submitButtonLabel,
    backButtonPath
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const dialogs = useDialogs();
  const notifications = useNotifications();
  const navigate = useNavigate();

  const [deleteProductInventoryItemById] = useMutation(
    DELETE_PRODUCT_INVENTORY_ITEM_BY_ID_MUTATION, {
    refetchQueries: [{ query: GET_PRODUCTS_QUERY }, { query: GET_PRODUCT_BY_ID_QUERY, variables: { id: productId } }],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSubmit = useCallback(
    async (event: SubmitEvent) => {
      event.preventDefault();

      setIsSubmitting(true);

      try {
        await onSubmit(
          formValues);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onSubmit, formValues],
  );

  const handleDelete = useCallback(async () => {
    if ((productInventoryItemId || 0) <= 0) {
      return;
    }

    const confirmed = await dialogs.confirm(
      `Do you wish to delete this inventory item?`,
      {
        title: `Delete inventory item?`,
        severity: 'error',
        okText: 'Delete',
        cancelText: 'Cancel',
      },
    );

    if (confirmed) {
      setIsDeleting(true);
      try {
        const { data: result } = await deleteProductInventoryItemById({ variables: { id: productInventoryItemId || 0 } });

        if (result?.deleted) {
          navigate(`/dashboard/products/${productId}/product-inventory-items`);

          notifications.show('Product inventory item deleted successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });
        } else {
          notifications.show(
            'Failed to delete product inventory item. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (deleteError) {
        notifications.show(
          `Failed to delete product inventory item. Reason: ' ${(deleteError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
      setIsDeleting(false);
    }
  }, [productInventoryItemId, dialogs, deleteProductInventoryItemById, navigate, productId, notifications]);

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFieldChange(
        event.target.name as keyof ProductInventoryItemFormState['values'],
        event.target.value,
      );
    },
    [onFieldChange],
  );

  const handleNumberFieldChange = useCallback(
    async (value: number | null, fieldName: string) => {
      onFieldChange(
        fieldName as keyof ProductInventoryItemFormState['values'],
        value || 0
      );
    },
    [onFieldChange],
  );

  const handleReset = useCallback(() => {
    if (onReset) {
      onReset(formValues);
    }
  }, [formValues, onReset]);

  const handleBack = useCallback(() => {
    navigate(backButtonPath ?? `/dashboard/products/${productId}/product-inventory-items`);
  }, [navigate, backButtonPath, productId]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
    >
      <FormGroup>
        <Grid container spacing={1} sx={{ mb: 2, width: '100%' }}>
          <Grid size={{ xs: 6, sm: 2 }} sx={{ display: 'flex' }}>
            <TextField
              select
              value={String(formValues?.packagingType ?? '0')}
              onChange={handleTextFieldChange}
              label="Packaging Type"
              name="packagingType"
              error={!!formErrors.packagingType}
              fullWidth
              required
            >
              <MenuItem value={PackagingType.JAR}>Jar</MenuItem>
              <MenuItem value={PackagingType.POUCH}>Pouch</MenuItem>
              <MenuItem value={PackagingType.BULK}>Bulk</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 9, sm: 3 }} sx={{ display: 'flex' }}>
            <TextField
              select
              value={String(formValues?.batchId ?? '0')}
              onChange={handleTextFieldChange}
              label="Batch"
              name="batchId"
              error={!!formErrors.batchId}
              fullWidth
              required
            >
              {batches?.map((batch) => (
                <MenuItem key={batch.id} value={String(batch.id)}>
                  {batch.lotNumber}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }} sx={{ display: 'flex' }}>
            <NumberField
              value={formValues?.quantityOnHand || null}
              onValueChange={(event) => handleNumberFieldChange(event, 'quantityOnHand')}
              name="quantityOnHand"
              label={formValues?.packagingType === PackagingType.BULK ? 'Pounds on Hand' : 'Qty on Hand'}
              weightFieldUnits={formValues?.packagingType === PackagingType.BULK ? 'lbs' : undefined}
              fullWidth
            />
          </Grid>
        </Grid>
      </FormGroup>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          {(productInventoryItemId || 0) > 0 && (
            <Button
              variant="contained"
              color="error"
              size="large"
              loading={isSubmitting || isDeleting}
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            size="large"
            color="success"
            startIcon={<SaveIcon />}
            loading={isSubmitting}
          >
            {submitButtonLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
